import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { usePermissions, useQueryParams } from "../contexts";
import { getQueryType, getRequestOptions } from "../helper-functions";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import {
    Claims,
    DefaultPagedResponse,
    Entity,
    Office,
    IUseQueryPaged,
    PagedResponse,
    QueryHookParams,
    QueryType,
} from "../models";
import { Operation } from "rfc6902";

export const useOfficeQuery = (params?: QueryHookParams): IUseQueryPaged<Office> => {
    const OfficeId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const { hasPermission } = usePermissions();
    const { get, post, patch, del } = useAxios();
    const { queryParams, sortParams, pageParams } = useQueryParams().getParamsApi(Entity.Offices);
    const url = Endpoints.Offices;
    const queryType = getQueryType(OfficeId, params?.queryType);

    const pagedListQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<Office>>(getRequestOptions(url, config, queryParams, sortParams, pageParams)),
        queryKey: queryKeys.offices.pagedList({ ...queryParams, ...pageParams, ...sortParams }),
        placeholderData: DefaultPagedResponse,
        enabled: hasPermission(Claims.ViewAccount) && queryType === QueryType.PagedList,
    });

    const listQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<Office>>(
                getRequestOptions(url, config, undefined, sortParams, undefined, false, true)
            ).then((response) => response.items),
        queryKey: queryKeys.offices.list({ ...sortParams }),
        placeholderData: [],
        enabled: hasPermission(Claims.ViewAccount) && queryType === QueryType.List,
    });

    const detailQuery = useGenericQuery({
        queryFn: (config) => get<Office>({ url: `${url}/${OfficeId}`, config }),
        queryKey: queryKeys.offices.detail(OfficeId),
        placeholderData: undefined,
        enabled: hasPermission(Claims.ViewAccount) && queryType === QueryType.Detail && !!params?.id,
    });

    const createMutation = useGenericMutation({
        mutationFn: (data: Partial<Office>) => post<Partial<Office>, Office>({ url, data }),
        queryKey: queryKeys.offices.all(),
        keysToInvalidate: [queryKeys.offices.lists(), queryKeys.offices.pagedLists(), queryKeys.territories.list()],
        successToast: "Office created",
    });

    const updateMutation = useGenericMutation({
        mutationFn: (data: Operation[]) => patch<Operation[], Office>({ url: `${url}/${OfficeId}`, data }),
        queryKey: queryKeys.offices.detail(OfficeId),
        keysToInvalidate: [queryKeys.offices.lists(), queryKeys.offices.pagedLists(), queryKeys.territories.list()],
        successToast: "Office updated",
    });

    const deleteMutation = useGenericMutation({
        mutationFn: () => del({ url: `${url}/${OfficeId}` }),
        queryKey: queryKeys.offices.detail(OfficeId),
        keysToInvalidate: [queryKeys.offices.lists(), queryKeys.offices.pagedLists(), queryKeys.territories.list()],
        successToast: "Office deleted",
    });

    return useMemo<IUseQueryPaged<Office>>(
        () => ({
            pagedListQuery,
            listQuery,
            detailQuery,
            createMutation,
            updateMutation,
            deleteMutation,
        }),
        [pagedListQuery, listQuery, detailQuery, createMutation, updateMutation, deleteMutation]
    );
};
